import { convertQuestions, convertAnswers, getAnswerFields } from '@/views/question-editor/format/convert'
import { recoveryAnswers } from '@/views/question-editor/format/recovery'
import scrollView from '../lib/scroll-view'
import * as file from '../lib/file'
import evidence from './evidence'
import indexes from './indexes'
import jump from './jump'
import option from './option'
import refer from './refer'
import review from './review'

class Review {
  constructor (questions, answers) {
    // 初始化问题
    this.questions = []
    this.originalAnswers = []
    this.answers = []
    // ID 索引
    this.indexes = {}
    // 创建实例时的初始化
    if (questions) {
      this.init(questions, answers)
    }
    this.extendedStepsStart = 0
    return this
  }

  init (questions, answers) {
    // 题目列表
    this.questions = convertQuestions(questions)
    // 是否存在答案
    if (answers) {
      this.answers = convertAnswers(this.questions, answers)
      this.originalAnswers = JSON.parse(JSON.stringify(this.answers))
    } else {
      // 置入空的答案模型
      this.questions.forEach((question) => {
        this.answers.push(getAnswerFields(question.type))
      })
    }

    // 生成题目选项索引
    this.createAllIndexes()

    return this
  }

  getQuestions () {
    return this.questions
  }

  getValidQuestions () {
    let questions = []
    this.questions.forEach(question => {
      if (!this.indexes[question.id].isJumped) {
        questions.push(question)
      }
    })
    return questions
  }

  getQuestion (id) {
    return this.indexes[id]
  }

  /**
   * 获得问题选项
   * @param {string} id
   */
  getOption (id) {
    return this.indexes[id]
  }

  /**
   * 获得所有答案
   * @returns {*}
   */
  getAnswers () {
    return this.answers
  }

  /**
   * 获得所有答案
   * 恢复转换前的数据格式
   * @param reviewPage
   * @returns {*}
   */
  getRecoveryAnswers (reviewPage) {
    const answers = recoveryAnswers(this.questions, this.answers, this.indexes)
    if (reviewPage && reviewPage.firstAnswerPhotos && reviewPage.firstAnswerPhotos.length > 0) {
      answers[0].photos = reviewPage.firstAnswerPhotos
    }
    if (reviewPage && reviewPage.orderDetail) {
      // 二维码题型 审核问题处理
      // reviewPage 可能为空 在 OrderConfirm.vue 文件中 calculateIsInvalidForTaskGroup 方法
      this.handleBanquetQrcodeReview(reviewPage, answers)
    }
    return answers
  }

  /**
   * 处理宴席订单的二维码题型审核问题
   * @param {Object} reviewPage 审核页面数据
   * @param {Array} answers 答案数组
   */
  handleBanquetQrcodeReview (reviewPage, answers) {
    // 宴席的订单 二维码题型 审核问题处理
    if (reviewPage.orderDetail.type === '23') {
      // 找到二维码
      const questions = reviewPage.questions.filter(item => item.evidenceType === 'qrcode')
      if (questions.length > 0) {
        const qrcodeQuestion = questions[0]
        // 查到二维码关联的宴席填空题
        const reportedTablesAssociationStepId = qrcodeQuestion.reportedTablesAssociationStepId
        const answer = answers.filter(item => item.stepId === reportedTablesAssociationStepId && item.isPassed === '30')
        // 不通过的 宴席填空题，强制 二维码题也不能通过，需要修改
        if (answer.length > 0) {
          const qrcodeAnswers = answers.filter(item => item.stepId === qrcodeQuestion.id)
          if (qrcodeAnswers.length > 0) {
            qrcodeAnswers[0].isPassed = '30'
          }
        }
      }
    }
  }

  /**
   * 设置答案数据
   * @param data
   */
  setAnswers (answers) {
    for (let i = 0; i < this.answers.length; i++) {
      for (let j in this.answers[i]) {
        if (this.answers[i].hasOwnProperty(j)) {
          if (this.answers[i][j] instanceof Array) {
            this.answers[i][j].splice(0, this.answers[i][j].length)
            this.answers[i][j].push.apply(this.answers[i][j], answers[i][j])
          } else {
            this.answers[i][j] = answers[i][j]
          }
        }
      }
    }
    this.createAllIndexes()
  }

  getAnswer (id) {
    return this.indexes[id].answer
  }

  /**
   * 设置隔断题号
   */
  setExtendedStepsStart (num) {
    this.extendedStepsStart = num
  }

  getExtendedStepsStart () {
    return this.extendedStepsStart
  }

  getAnswerMarketPhotos () {
    const answer = this.answers[1]
    // WARNING: 该选项在将来可能会发生变化
    if (!answer.selectedOptionId.includes('R1515570260035')) {
      return []
    }
    return answer.evidences.map((val) => {
      return {
        src: val.src,
        selected: false
      }
    })
  }

  /**
   * 获得最小结算金额
   * @param amount 当前金额
   * @returns {number}
   */
  getMiniSettlementAmount (amount) {
    let miniAmount = Infinity
    let settlementAmountRatio = 0
    let settlementType = 0
    this.getValidQuestions().forEach((question) => {
      // 仅选择题
      if (this.isChoice(question.id)) {
        let answer = this.getAnswer(question.id)
        question.options.forEach((option) => {
          // 仅被选中的选项
          if (answer.selectedOptionId.includes(option.id)) {
            if (option.settlementType === 1) { // 金额比例
              let settlementAmount = option.settlementAmountRatio * amount
              if (settlementAmount < miniAmount) {
                miniAmount = settlementAmount
                settlementType = option.settlementType
                settlementAmountRatio = option.settlementAmountRatio
              }
            } else if (option.settlementType === 2) { // 固定金额
              if (option.settlementAmount < miniAmount) {
                miniAmount = option.settlementAmount
                settlementType = option.settlementType
                settlementAmountRatio = 0
              }
            }
          }
        })
      }
    })

    // 预设金额不能大于工单金额
    if (miniAmount !== Infinity && miniAmount > amount) {
      return {amount: amount, type: 2, ratio: 0}
    }

    return {amount: miniAmount, type: settlementType, ratio: settlementAmountRatio}
  }

  /**
   * 获得选择题变更选项的题目index列表
   */
  getIndexesForChangeSelected () {
    let indexes = []
    this.questions.forEach((question, index) => {
      if (this.isChoice(question.id)) {
        let answer = this.answers[index]
        let originalAnswers = this.originalAnswers[index]
        if (!_.isEqual(answer.selectedOptionId.sort(), originalAnswers.selectedOptionId.sort())) {
          indexes.push(index)
        }
      }
    })
    return indexes
  }
}

// 模块内容
Object.assign(Review.prototype, file, scrollView, evidence, indexes, jump, option, refer, review)

export default Review



// WEBPACK FOOTER //
// ./src/views/question-review/review/index.js